import type { EngineError, EngineEvent, LoadOptions, MediaTrack, PlayerEngine, StreamCandidate } from '../engine';
import type { HlsLike, HlsLoaderCallbacks, HlsLoaderClass, HlsLoaderContext, MpegtsLike, MpegtsPlayerLike } from './libraries';
import { loadHls, loadMpegts } from './libraries';

export interface WebVideoEngineOptions {
  /**
   * Rewrites each network request (playlist, segments, TS stream). Used in
   * development to route through the CORS proxy; identity in packaged apps.
   */
  requestUrl?: (url: string) => string;
  /**
   * Larger buffers for slow or long network paths (the browser version goes
   * through a relay): more ahead of the playhead, at some memory cost.
   * Off on TVs, whose memory budgets the defaults are sized for.
   */
  largeBuffers?: boolean;
}

/** HLS buffer sizes with `largeBuffers`. */
const HLS_LARGE_BUFFERS = { maxBufferLength: 60, maxMaxBufferLength: 120, backBufferLength: 30, liveSyncDurationCount: 4 };
/** Live MPEG-TS drift bounds (seconds behind the buffered end) and catch-up speed. */
const CATCH_UP_ABOVE_S = 30;
const CATCH_UP_UNTIL_S = 15;
const CATCH_UP_RATE = 1.1;
/** Going live on MPEG-TS: stop this far before the buffered end, so playback doesn't stall at once. */
const LIVE_EDGE_MARGIN_S = 3;

/** MPEG-TS: a larger first stash absorbs bursts and gaps on a slow path. */
const MPEGTS_LARGE_BUFFERS = { stashInitialSize: 2 * 1024 * 1024 };

/** How long a progressive file may wait before its relay pass is checked (#238). */
const PASS_RENEW_AFTER_MS = 1500;
/** Relay passes last 3 minutes; past this age the one a file was opened with may already be refused. */
const PASS_OLD_AFTER_MS = 150_000;

/**
 * Buffer settings sized for TV memory budgets: a short forward buffer for
 * fast zapping and a small back buffer so memory stays flat over long
 * sessions.
 */
const HLS_CONFIG = {
  enableWorker: true,
  // Zap speed: start at the level the default bandwidth estimate picks
  // instead of probing with the lowest level first (which delays the first
  // frame by a whole extra segment), and fetch the first fragment early.
  // Quality adapts upward while playing.
  testBandwidth: false,
  startFragPrefetch: true,
  maxBufferLength: 20,
  maxMaxBufferLength: 40,
  backBufferLength: 10,
  liveSyncDurationCount: 3,
  manifestLoadingMaxRetry: 1,
  levelLoadingMaxRetry: 2,
  fragLoadingMaxRetry: 3,
  startLevel: -1,
  // A conservative first estimate picks a small first segment (fast first
  // frame); ABR raises quality within a few segments.
  abrEwmaDefaultEstimate: 500_000,
};

/**
 * IPTV servers deliver live TS in bursts. The buffer must absorb them, so
 * there is no latency chasing that jumps to the live edge (that left ~2 s of
 * buffer and stalled on every burst gap). Drift is bounded gently instead, by
 * the engine (not mpegts.js, so it can be switched off while the viewer has
 * paused live TV): beyond 30 s behind live, playback speeds up slightly (no
 * jump) until it is 15 s behind.
 *
 * `enableWorker` stays off: mpegts.js builds its worker by extracting its
 * own webpack modules into a Blob, which fails silently once the library is
 * re-bundled (Vite/esbuild): MediaSource opens but no data is ever appended.
 */
const MPEGTS_CONFIG = {
  enableWorker: false,
  enableStashBuffer: true,
  lazyLoad: false,
  liveBufferLatencyChasing: false,
  liveSync: false,
  autoCleanupSourceBuffer: true,
  autoCleanupMaxBackwardDuration: 30,
  autoCleanupMinBackwardDuration: 15,
};

/**
 * HTML5 video engine: hls.js for HLS where MSE exists (native HLS
 * otherwise), mpegts.js for raw MPEG-TS, and the plain `<video>` element
 * for progressive files. Libraries are loaded on first use.
 */
export class WebVideoEngine implements PlayerEngine {
  private readonly listeners = new Set<(event: EngineEvent) => void>();
  private readonly requestUrl: (url: string) => string;
  private readonly largeBuffers: boolean;
  private live = false;
  private catchUp = true;
  /** Set while a pause comes from our own stop or reload, until the next stream plays. */
  private ownPause = false;
  private hls: HlsLike | null = null;
  private mpegts: MpegtsPlayerLike | null = null;
  private generation = 0;
  private mediaRecoveries = 0;
  /**
   * Viewer's subtitle choice for the current stream: a track id, `null` for
   * off, or `undefined` before any choice. Until the viewer chooses, the
   * stream's own default subtitle (HLS `DEFAULT=YES`) is honoured and
   * embedded closed captions stay off, so the two never overlap.
   */
  private textChoice: string | null | undefined = undefined;
  /** A progressive file playing: its address before `requestUrl`, the address given to the element, the pending pass check. */
  private fileUrl: string | null = null;
  // Kept as given: `video.src` reads back normalised (":80" dropped, host lower-cased), so it can't be compared.
  private fileSrc = '';
  private passCheck: ReturnType<typeof setTimeout> | null = null;
  private fileSrcAt = 0;
  private readonly detachVideo: () => void;

  constructor(
    private readonly video: HTMLVideoElement,
    options: WebVideoEngineOptions = {},
  ) {
    this.requestUrl = options.requestUrl ?? ((url) => url);
    this.largeBuffers = options.largeBuffers ?? false;
    this.detachVideo = this.attachVideoEvents();
  }

  load(source: StreamCandidate, options: LoadOptions): void {
    this.teardown();
    const generation = ++this.generation;
    this.live = options.live;
    this.catchUp = true;
    this.video.playbackRate = 1;
    this.mediaRecoveries = 0;
    this.textChoice = undefined;
    const start = (body: () => Promise<void>) =>
      body().catch((error: unknown) => {
        if (generation === this.generation) this.emitError({ kind: 'unsupported', detail: `player library failed to load: ${String(error)}` });
      });
    if (source.kind === 'hls') start(() => this.loadHls(source.url, generation));
    else if (source.kind === 'ts') start(() => this.loadTs(source.url, options, generation));
    else {
      this.fileUrl = source.url;
      this.fileSrc = this.requestUrl(source.url);
      this.fileSrcAt = Date.now();
      this.video.src = this.fileSrc;
      if (options.startAt) this.video.currentTime = options.startAt;
    }
  }

  play(): void {
    const result = this.video.play() as Promise<void> | undefined;
    // Autoplay refusals and interrupted loads are not stream failures.
    result?.catch(() => undefined);
  }

  pause(): void {
    // The viewer's pause: always reported, even right after our own reload (which marks its pause as ours).
    this.ownPause = false;
    this.video.pause();
  }

  seek(seconds: number): void {
    const duration = this.video.duration;
    const target = Number.isFinite(duration) ? Math.min(seconds, Math.max(0, duration - 0.5)) : seconds;
    this.video.currentTime = Math.max(0, target);
  }

  liveLatency(): number | null {
    if (!this.live) return null;
    const now = this.video.currentTime;
    // hls.js knows the live position from the playlist, even beyond what is buffered.
    const sync = this.hls?.liveSyncPosition;
    if (typeof sync === 'number' && Number.isFinite(sync)) return Math.max(0, sync - now);
    const end = bufferedEndOf(this.video);
    return end === null ? null : Math.max(0, end - now);
  }

  goLive(): void {
    if (!this.live) return;
    const sync = this.hls?.liveSyncPosition;
    const end = bufferedEndOf(this.video);
    const target = typeof sync === 'number' && Number.isFinite(sync) ? sync : end === null ? null : end - LIVE_EDGE_MARGIN_S;
    if (target !== null && target > this.video.currentTime) this.video.currentTime = target;
    this.setLiveCatchUp(true);
    this.play();
  }

  setMuted(muted: boolean): void {
    this.video.muted = muted;
  }

  isMuted(): boolean {
    return this.video.muted;
  }

  setLiveCatchUp(enabled: boolean): void {
    this.catchUp = enabled;
    if (!enabled && this.video.playbackRate !== 1) this.video.playbackRate = 1;
  }

  /** Live MPEG-TS: speeds up slightly when far behind (see MPEGTS_CONFIG), unless the viewer chose to be behind. */
  private boundDrift(): void {
    if (!this.live || !this.mpegts) return;
    const end = bufferedEndOf(this.video);
    const behind = end === null ? 0 : end - this.video.currentTime;
    const rate = this.catchUp && (behind > CATCH_UP_ABOVE_S || (this.video.playbackRate > 1 && behind > CATCH_UP_UNTIL_S)) ? CATCH_UP_RATE : 1;
    if (this.video.playbackRate !== rate) this.video.playbackRate = rate;
  }

  stop(): void {
    this.generation++;
    this.teardown();
  }

  destroy(): void {
    this.stop();
    this.detachVideo();
    this.listeners.clear();
  }

  on(listener: (event: EngineEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  audioTracks(): MediaTrack[] {
    if (this.hls) {
      return this.hls.audioTracks.map((track, index) => ({
        id: String(index),
        label: track.name || track.lang || `Audio ${index + 1}`,
        language: track.lang,
        active: index === this.hls?.audioTrack,
      }));
    }
    const native = (this.video as HTMLVideoElement & { audioTracks?: ArrayLike<{ id: string; label: string; language: string; enabled: boolean }> }).audioTracks;
    if (!native) return [];
    return Array.from(native, (track, index) => ({
      id: track.id || String(index),
      label: track.label || track.language || `Audio ${index + 1}`,
      language: track.language,
      active: track.enabled,
    }));
  }

  /**
   * Subtitle choices: hls.js subtitle renditions (`hls:<n>`) plus text tracks
   * on the element (`el:<n>`), which include closed captions carried inside
   * the video (CEA-608/708) and native-HLS subtitles.
   */
  textTracks(): MediaTrack[] {
    const tracks: MediaTrack[] = [];
    if (this.hls) {
      this.hls.subtitleTracks.forEach((track, index) => {
        tracks.push({
          id: `hls:${index}`,
          label: track.name || track.lang || `Subtitles ${index + 1}`,
          language: track.lang,
          active: index === this.hls?.subtitleTrack && this.hls.subtitleDisplay,
        });
      });
    }
    this.elementTextTracks().forEach(({ track, index }) => {
      tracks.push({
        id: `el:${index}`,
        label: track.kind === 'captions' ? `${track.label || track.language || 'Captions'} (CC)` : track.label || track.language || `Subtitles ${index + 1}`,
        language: track.language,
        active: track.mode === 'showing',
      });
    });
    return tracks;
  }

  /**
   * Element text tracks the user can choose. With hls.js, its subtitle
   * renditions also appear here as element tracks; those are controlled
   * through hls.js and skipped to avoid listing them twice.
   */
  private elementTextTracks(): { track: TextTrack; index: number }[] {
    const all = Array.from(this.video.textTracks).map((track, index) => ({ track, index }));
    return all.filter(({ track }) => (this.hls ? track.kind === 'captions' : track.kind === 'subtitles' || track.kind === 'captions'));
  }

  selectAudio(id: string): void {
    if (this.hls) {
      this.hls.audioTrack = Number(id);
    } else {
      const native = (this.video as HTMLVideoElement & { audioTracks?: ArrayLike<{ id: string; enabled: boolean }> }).audioTracks;
      if (native) Array.from(native).forEach((track, index) => (track.enabled = (track.id || String(index)) === id));
    }
    this.emit({ type: 'tracks' });
  }

  selectText(id: string | null): void {
    this.textChoice = id;
    this.applyTextChoice();
    this.emit({ type: 'tracks' });
  }

  /**
   * Applies the viewer's choice; streams add tracks during playback (captions
   * appear when the first caption data is parsed), so this runs again then.
   */
  private applyTextChoice(): void {
    const id = this.textChoice;
    if (id === undefined) {
      this.elementTextTracks().forEach(({ track }) => {
        if (track.kind === 'captions' && track.mode !== 'disabled') track.mode = 'disabled';
      });
      return;
    }
    const [source, value] = id === null ? [null, -1] : [id.slice(0, id.indexOf(':')), Number(id.slice(id.indexOf(':') + 1))];
    if (this.hls) {
      const useHls = source === 'hls';
      this.hls.subtitleTrack = useHls ? value : -1;
      this.hls.subtitleDisplay = useHls;
    }
    this.elementTextTracks().forEach(({ track, index }) => {
      const mode = source === 'el' && index === value ? 'showing' : 'disabled';
      if (track.mode !== mode) track.mode = mode;
    });
  }

  private async loadHls(url: string, generation: number): Promise<void> {
    const Hls = await loadHls();
    if (generation !== this.generation) return;
    if (!Hls.isSupported()) {
      if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
        this.video.src = this.requestUrl(url);
        this.play();
        return;
      }
      this.emitError({ kind: 'unsupported', detail: 'HLS is not supported' });
      return;
    }
    const hls = new Hls({ ...HLS_CONFIG, ...(this.largeBuffers ? HLS_LARGE_BUFFERS : {}), loader: rewritingLoader(Hls.DefaultConfig.loader, this.requestUrl) });
    this.hls = hls;
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this.applyTextChoice();
      this.emit({ type: 'tracks' });
      this.play();
    });
    hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, () => this.emit({ type: 'tracks' }));
    hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, () => {
      this.applyTextChoice();
      this.emit({ type: 'tracks' });
    });
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal || generation !== this.generation) return;
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && this.mediaRecoveries < 1) {
        this.mediaRecoveries++;
        hls.recoverMediaError();
        return;
      }
      const status = data.response?.code;
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        this.emitError({ kind: 'network', status: status || undefined, detail: data.details });
      } else if (data.details === 'manifestIncompatibleCodecsError' || data.details === 'bufferIncompatibleCodecsError') {
        this.emitError({ kind: 'unsupported', detail: data.details });
      } else {
        this.emitError({ kind: 'media', detail: data.details });
      }
    });
    hls.loadSource(url);
    hls.attachMedia(this.video);
  }

  private async loadTs(url: string, options: LoadOptions, generation: number): Promise<void> {
    const mpegts: MpegtsLike = await loadMpegts();
    if (generation !== this.generation) return;
    if (!mpegts.isSupported()) {
      this.emitError({ kind: 'unsupported', detail: 'MPEG-TS playback needs Media Source Extensions' });
      return;
    }
    const player = mpegts.createPlayer({ type: 'mpegts', isLive: options.live, url: this.requestUrl(url) }, { ...MPEGTS_CONFIG, ...(this.largeBuffers ? MPEGTS_LARGE_BUFFERS : {}) });
    this.mpegts = player;
    player.on(mpegts.Events.ERROR, (type: string, detail: string, info?: { code?: number; msg?: string }) => {
      if (generation !== this.generation) return;
      if (type === mpegts.ErrorTypes.NETWORK_ERROR) {
        const status = detail === mpegts.ErrorDetails.NETWORK_STATUS_CODE_INVALID ? info?.code : undefined;
        this.emitError({ kind: 'network', status, detail });
      } else if (detail === mpegts.ErrorDetails.MEDIA_CODEC_UNSUPPORTED || detail === mpegts.ErrorDetails.MEDIA_FORMAT_UNSUPPORTED) {
        this.emitError({ kind: 'unsupported', detail });
      } else if (detail === mpegts.ErrorDetails.MEDIA_MSE_ERROR && /unsupported|not supported/i.test(info?.msg ?? '')) {
        // The browser rejected a track's codec (e.g. Dolby AC-3 audio).
        this.emitError({ kind: 'unsupported', detail: 'codec not supported by this browser' });
      } else {
        this.emitError({ kind: 'media', detail });
      }
    });
    player.attachMediaElement(this.video);
    player.load();
    this.play();
  }

  private teardown(): void {
    // Stopping or reloading pauses the element: that pause is ours, not the viewer's or another app's.
    this.ownPause = true;
    this.fileUrl = null;
    if (this.passCheck) clearTimeout(this.passCheck);
    this.passCheck = null;
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }
    if (this.mpegts) {
      try {
        this.mpegts.pause();
        this.mpegts.unload();
        this.mpegts.detachMediaElement();
      } finally {
        this.mpegts.destroy();
        this.mpegts = null;
      }
    }
    this.video.removeAttribute('src');
    this.video.load();
  }

  /**
   * A progressive file keeps the address it was opened with, and the browser asks for each next part
   * with it. Through the relay that address carries a pass that expires after a few minutes
   * (renewed by the app's heartbeat), so the relay refuses later parts and the video waits for good.
   * Still waiting a moment later with a newer pass available: reopen at the same spot with it (#238).
   */
  private renewPassWhenStuck(): void {
    if (this.fileUrl === null || this.passCheck) return;
    const generation = this.generation;
    this.passCheck = setTimeout(() => {
      this.passCheck = null;
      const video = this.video;
      if (generation !== this.generation || this.fileUrl === null || video.paused || video.readyState >= 3) return;
      const fresh = this.requestUrl(this.fileUrl);
      // A new pass comes every 30 s, but the one given lasts 3 min: reopen only once it is nearly
      // spent, so a slow line isn't reopened for nothing and a reopen can't loop.
      if (fresh === this.fileSrc || Date.now() - this.fileSrcAt < PASS_OLD_AFTER_MS) return;
      const position = video.currentTime;
      this.ownPause = true;
      this.fileSrc = fresh;
      this.fileSrcAt = Date.now();
      video.src = fresh;
      video.currentTime = position;
      this.play();
    }, PASS_RENEW_AFTER_MS);
  }

  private attachVideoEvents(): () => void {
    const video = this.video;
    const handlers: [string, () => void][] = [
      [
        'playing',
        () => {
          this.ownPause = false;
          this.emit({ type: 'playing' });
        },
      ],
      [
        'waiting',
        () => {
          this.emit({ type: 'waiting' });
          this.renewPassWhenStuck();
        },
      ],
      [
        'pause',
        () => {
          // Not reported: pauses from stopping or reloading a stream (reconnects), and the pause the
          // browser makes when a stream runs out (an "ended" follows). Only real pauses remain:
          // the viewer's, or another app's (a call).
          if (this.ownPause || video.ended) return;
          this.emit({ type: 'paused' });
        },
      ],
      ['ended', () => this.emit({ type: 'ended' })],
      ['loadedmetadata', () => this.emit({ type: 'tracks' })],
      [
        'timeupdate',
        () => {
          this.boundDrift();
          this.emit({ type: 'time', position: video.currentTime, duration: video.duration });
        },
      ],
      [
        'error',
        () => {
          // hls.js and mpegts.js report their own errors with more detail.
          if (this.hls || this.mpegts || !video.error) return;
          const code = video.error.code;
          if (code === 4) this.emitError({ kind: 'unsupported', detail: video.error.message || 'source not supported' });
          else if (code === 2) this.emitError({ kind: 'network', detail: video.error.message });
          else this.emitError({ kind: 'media', detail: video.error.message });
        },
      ],
    ];
    for (const [name, handler] of handlers) video.addEventListener(name, handler);
    const onAddTrack = () => {
      this.applyTextChoice();
      this.emit({ type: 'tracks' });
    };
    video.textTracks.addEventListener('addtrack', onAddTrack);
    return () => {
      for (const [name, handler] of handlers) video.removeEventListener(name, handler);
      video.textTracks.removeEventListener('addtrack', onAddTrack);
    };
  }

  /**
   * Errors are reported on the next tick: listeners tear the stream down,
   * and destroying hls.js/mpegts.js inside their own error callbacks makes
   * them throw.
   */
  private emitError(error: EngineError): void {
    const generation = this.generation;
    setTimeout(() => {
      if (generation === this.generation) this.emit({ type: 'error', error });
    }, 0);
  }

  private emit(event: EngineEvent): void {
    for (const listener of this.listeners) listener(event);
  }
}

/**
 * Wraps hls.js' loader so requests go to `requestUrl(url)` while hls.js keeps
 * seeing the real URL. A proxy reports the post-redirect URL in
 * `x-proxy-final-url`; hls.js uses it to resolve relative segment URLs.
 */
export function rewritingLoader(Base: HlsLoaderClass, requestUrl: (url: string) => string): HlsLoaderClass {
  return class RewritingLoader extends Base {
    override load(context: HlsLoaderContext, config: object, callbacks: HlsLoaderCallbacks): void {
      const original = context.url;
      const rewritten = requestUrl(original);
      if (rewritten === original) {
        super.load(context, config, callbacks);
        return;
      }
      context.url = rewritten;
      super.load(context, config, {
        ...callbacks,
        onSuccess: (response, stats, loaded, networkDetails) => {
          context.url = original;
          const finalUrl = finalUrlOf(networkDetails) ?? original;
          callbacks.onSuccess({ ...response, url: finalUrl }, stats, loaded, networkDetails);
        },
      });
    }
  };
}

function finalUrlOf(networkDetails: unknown): string | null {
  if (networkDetails && typeof (networkDetails as XMLHttpRequest).getResponseHeader === 'function') {
    return (networkDetails as XMLHttpRequest).getResponseHeader('x-proxy-final-url');
  }
  if (networkDetails && typeof (networkDetails as Response).headers?.get === 'function') {
    return (networkDetails as Response).headers.get('x-proxy-final-url');
  }
  return null;
}

/** End of the buffered range that contains the playhead (or the last one), in seconds. */
function bufferedEndOf(video: HTMLVideoElement): number | null {
  const ranges = video.buffered;
  if (ranges.length === 0) return null;
  for (let i = 0; i < ranges.length; i++) {
    if (ranges.start(i) <= video.currentTime + 0.5 && video.currentTime <= ranges.end(i)) return ranges.end(i);
  }
  return ranges.end(ranges.length - 1);
}
